import { styled } from '@linaria/react';
import { Loader, Paper, Portal, ScrollArea, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SlashCommand } from '../../../../../api/agents-client/agent-client.type';
import { newCOLORS } from '../../../../../styles/colors';
import { getCommandScope } from './agent-commands.helpers';
import { SCOPE_BADGE_BG, SCOPE_BADGE_COLORS, SCOPE_BADGE_LABEL } from './agent-commands.helpers';
import type { GroupedCommands } from './agent-commands.type';

interface SlashCommandPopoverProps {
  isOpen: boolean;
  groups: GroupedCommands[];
  flatCommands: SlashCommand[];
  searchQuery: string;
  isLoading?: boolean;
  onSelect: (command: SlashCommand) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  dataTheme?: string;
}

export function SlashCommandPopover({
  isOpen,
  groups,
  flatCommands,
  searchQuery,
  isLoading,
  onSelect,
  onClose,
  anchorRef,
  dataTheme,
}: SlashCommandPopoverProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 0 });
  const listRef = useRef<HTMLDivElement>(null);

  // Build a flat list for keyboard navigation
  const flatList = useMemo((): SlashCommand[] => {
    if (searchQuery) return flatCommands;
    // When not searching, flatten groups in display order
    return groups.flatMap((g) => g.commands);
  }, [groups, flatCommands, searchQuery]);

  const groupedCommandsWithIndex = useMemo(
    () =>
      groups.reduce(
        (acc, group) => {
          const commands = group.commands.map((command) => {
            const indexedCommand = { command, index: acc.nextIndex };
            acc.nextIndex += 1;
            return indexedCommand;
          });

          acc.grouped.push({
            scope: group.scope,
            label: group.label,
            commands,
          });

          return acc;
        },
        {
          nextIndex: 0,
          grouped: [] as {
            scope: GroupedCommands['scope'];
            label: GroupedCommands['label'];
            commands: Array<{ command: SlashCommand; index: number }>;
          }[],
        },
      ).grouped,
    [groups],
  );

  // Reset index when popover opens or items change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on isOpen/flatList changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [isOpen, flatList.length]);

  // Calculate position based on anchor element
  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.top - 8,
        left: rect.left + 16,
        width: 320,
      });
    }
  }, [isOpen, anchorRef]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;
      const maxIndex = flatList.length - 1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
        case 'Tab': {
          e.preventDefault();
          const cmd = flatList[selectedIndex];
          if (cmd) onSelect(cmd);
          break;
        }
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [isOpen, flatList, selectedIndex, onSelect, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  const renderGrouped = () =>
    groupedCommandsWithIndex.map((group) => (
      <ScopeSection key={group.scope}>
        <ScopeHeader>
          <Text size="xs" c="dimmed" fw={500}>
            {group.label.toUpperCase()}
          </Text>
        </ScopeHeader>
        {group.commands.map(({ command, index }) => {
          const scope = getCommandScope(command);
          return (
            <CommandRow
              key={command.id}
              data-index={index}
              $isSelected={index === selectedIndex}
              onClick={() => onSelect(command)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <CommandInfo>
                <CommandNameText>/{command.name}</CommandNameText>
                {command.description && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {command.description}
                  </Text>
                )}
              </CommandInfo>
              <ScopeBadge
                style={{
                  color: SCOPE_BADGE_COLORS[scope],
                  background: SCOPE_BADGE_BG[scope],
                }}
              >
                <Text size="xs" fw={500} color="inherit">
                  {SCOPE_BADGE_LABEL[scope]}
                </Text>
              </ScopeBadge>
            </CommandRow>
          );
        })}
      </ScopeSection>
    ));

  const renderFlat = () =>
    flatCommands.map((cmd, idx) => {
      const scope = getCommandScope(cmd);
      return (
        <CommandRow
          key={cmd.id}
          data-index={idx}
          $isSelected={idx === selectedIndex}
          onClick={() => onSelect(cmd)}
          onMouseEnter={() => setSelectedIndex(idx)}
        >
          <CommandInfo>
            <CommandNameText>/{cmd.name}</CommandNameText>
            {cmd.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {cmd.description}
              </Text>
            )}
          </CommandInfo>
          <ScopeBadge
            style={{
              color: SCOPE_BADGE_COLORS[scope],
              background: SCOPE_BADGE_BG[scope],
            }}
          >
            <Text size="xs" fw={500} color="inherit">
              {SCOPE_BADGE_LABEL[scope]}
            </Text>
          </ScopeBadge>
        </CommandRow>
      );
    });

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <Loader size="sm" />
        </LoadingContainer>
      );
    }

    if (flatList.length === 0) {
      return (
        <EmptyState>
          <Text size="sm" c="dimmed">
            No commands found
          </Text>
        </EmptyState>
      );
    }

    return searchQuery ? renderFlat() : renderGrouped();
  };

  return (
    <Portal>
      <PopoverContainer
        data-theme={dataTheme}
        style={{
          top: popoverPosition.top,
          left: popoverPosition.left,
          width: popoverPosition.width,
        }}
      >
        <Paper
          shadow="md"
          radius="md"
          withBorder
          style={{
            background: 'var(--flora-popover-bg, white)',
            borderColor: 'var(--flora-popover-border, var(--mantine-color-default-border))',
          }}
        >
          <PopoverHeader>
            <Text size="xs" c="dimmed" fw={500}>
              {searchQuery ? `Commands matching "${searchQuery}"` : 'Slash Commands'}
            </Text>
          </PopoverHeader>
          <ScrollArea.Autosize mah={280} ref={listRef} scrollbars="y">
            <PopoverBody>{renderContent()}</PopoverBody>
          </ScrollArea.Autosize>
        </Paper>
      </PopoverContainer>
    </Portal>
  );
}

const PopoverContainer = styled.div`
  position: fixed;
  transform: translateY(-100%);
  z-index: 9999;
  max-height: 340px;
  min-width: 240px;
`;

const PopoverHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid var(--flora-popover-border, ${newCOLORS.lightGray});
`;

const PopoverBody = styled.div`
  padding: 6px;
`;

const ScopeSection = styled.div`
  &:not(:first-child) {
    margin-top: 4px;
  }
`;

const ScopeHeader = styled.div`
  padding: 4px 8px 2px;
`;

const CommandRow = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  background: ${(props) => (props.$isSelected ? `var(--flora-popover-hover, ${newCOLORS.lighterGray})` : 'transparent')};
  transition: background 0.12s ease;

  &:hover {
    background: var(--flora-popover-hover, ${newCOLORS.lighterGray});
  }
`;

const CommandInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const CommandNameText = styled.span`
  font-family: var(--font-mono, 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace);
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-darkest);
  line-height: 1.3;
`;

const ScopeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 10px;
  line-height: 1;
  flex-shrink: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;
